/**
 * SessionExpiredModal — modal that appears when the user's session has
 * timed out. It overlays the app, blocks interaction with stale UI, and
 * lets the user re-authenticate without losing in-progress work.
 *
 * Industry-standard pattern (Looker, Mode, Hex, Linear, Notion):
 *   • Non-dismissible (no mask click, no X)
 *   • Inline mini login form (same fields the user knows)
 *   • Optional "Sign out" escape hatch → routes to the regular login page
 *   • On success: modal closes, user lands back exactly where they were
 *
 * The state lives in AuthContext (`sessionExpired` + `dismissSessionExpired`).
 */
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, Divider, message } from 'antd';
import { LockOutlined, UserOutlined, WindowsFilled } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getEntraSsoConfig, startEntraLogin } from '../services/authService';

const { Text } = Typography;

export default function SessionExpiredModal() {
  const { sessionExpired, login, logout, dismissSessionExpired, userEmail } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [form] = Form.useForm();

  // Fetch SSO availability once the modal is needed. The config rarely
  // changes during a session, so a single fetch on first open is enough.
  useEffect(() => {
    if (!sessionExpired) return;
    let cancelled = false;
    getEntraSsoConfig()
      .then(cfg => { if (!cancelled) setSsoEnabled(Boolean(cfg && cfg.enabled)); })
      .catch(() => { /* leave SSO hidden on error */ });
    return () => { cancelled = true; };
  }, [sessionExpired]);

  // Pre-fill username/email if we know it (the user just got expired,
  // we still have their cached email from the last session).
  useEffect(() => {
    if (sessionExpired && userEmail) {
      form.setFieldsValue({ username: userEmail });
    }
  }, [sessionExpired, userEmail, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError('');
    try {
      const result = await login(values.username, values.password);
      if (result?.success) {
        message.success('Welcome back — you’re signed in.', 3);
        form.resetFields(['password']);
        dismissSessionExpired();
      } else {
        setError(result?.error || 'Sign-in failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => {
    // Hard logout: close the modal, clear auth state, and let the app's
    // top-level router send the user to the login page.
    dismissSessionExpired();
    logout();
  };

  return (
    <Modal
      open={sessionExpired}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <LockOutlined style={{ color: '#faad14' }} />
          Your session has timed out
        </span>
      }
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      width={420}
      // High z-index so the modal sits above any in-app drawers/panels
      // (e.g. the chart editor's translateX overlay).
      zIndex={2000}
      afterOpenChange={(open) => {
        if (open) {
          // Auto-focus the most useful field. If we already have the
          // username pre-filled, jump straight to password.
          setTimeout(() => {
            const target = userEmail
              ? document.querySelector('.dx-session-expired-modal input[type="password"]')
              : document.querySelector('.dx-session-expired-modal input[type="text"]');
            target?.focus();
          }, 50);
        }
      }}
      className="dx-session-expired-modal"
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        For your security, we’ve signed you out after a period of inactivity.
        Your work is preserved — sign in again to pick up where you left off.
      </Text>

      {ssoEnabled && (
        <>
          <Button
            block
            size="large"
            icon={<WindowsFilled style={{ color: '#0078D4' }} />}
            onClick={() => {
              // Preserve the user's current location so the SSO callback
              // lands them back exactly where the session expired.
              const ret = (window.location.pathname || '/') + (window.location.search || '');
              startEntraLogin(ret);
            }}
            data-testid="session-expired-entra-sso-button"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600, marginBottom: 8 }}
            disabled={submitting}
          >
            Sign in with Cox SSO
          </Button>
          <Divider plain style={{ margin: '12px 0', fontSize: 12, color: '#888' }}>or</Divider>
        </>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError('')}
          style={{ marginBottom: 12 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="on"
        requiredMark={false}
      >
        <Form.Item
          label="Username or email"
          name="username"
          rules={[{ required: true, message: 'Please enter your username or email' }]}
        >
          <Input
            prefix={<UserOutlined />}
            autoComplete="username"
            placeholder="you@example.com"
            disabled={submitting}
          />
        </Form.Item>
        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: true, message: 'Please enter your password' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="current-password"
            placeholder="••••••••"
            disabled={submitting}
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <Button type="link" onClick={handleSignOut} disabled={submitting} style={{ paddingLeft: 0 }}>
            Sign out
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Sign in again
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
